<template>
  <div v-if="registryResultsTableVisible && !registryResultsLoading" class="search-registry-results cell small-12 medium-12 large-12 xlarge-12">
    <div :class="`large registries-info ${registryResults.length < 1 ? 'no-registries-info' : ''}`">
      <span v-if="registryResults.length < 1">No registries found. Check the spelling or try a different search.</span>
      <span v-else-if="registryResults.length === 1">{{ `${registryResults.length} registry found` }}</span>
      <span v-else>{{ `${registryResults.length} registries found` }}</span>
    </div>
    <div v-if="registryResults.length > 0">
      <AtomicUiDataTable
        :key="dataTableKey"
        :columns="columns"
        :value="registryResultsPaginated"
        color="white"
        :sortable="true"
        content="html"
        selection-mode="single"
        class="margin-top-m registry-table"
        :size="!isMcom ? 'small' : ''"
        @row-select="onRegistrySelect"
        @sort="onRegistrySort"
      />
      <AtomicUiPagination
        :key="paginationKey"
        :total="registryResults.length"
        :default-page-size="20"
        :page-size-options="[20, 40]"
        alignment="pagination-alignment"
        class="registry-pagination"
        :class="{ 'pagination-hide': registryResults.length <= 1 }"
        @pagination-page-select="handlePageChange"
      />
    </div>
  </div>
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { registryFormatDate, paginate } from '~/utils/SearchRegistryUtil';
import { SET_SELECTED_REGISTRY_ID, SET_GIFTGIVER_TO_SEARCHPAGE, SET_SEARCH_TO_GIFTGIVERPAGE, SET_SELECTED_REGISTRY_GUID } from '~/types/mutations';
import { searchRegistryResult } from 'config';

import { useEnvPropsStore } from '~/stores/envProps';
import { useSearchRegistryStore } from '~/stores/searchRegistry';

const router = useRouter();
const columns = ref([
  {
    field: 'name',
    header: searchRegistryResult.name,
  },
  {
    field: 'occasionType',
    header: searchRegistryResult.occasionType,
  },
  {
    field: 'formattedOccasionDate',
    header: searchRegistryResult.formattedOccasionDate,
    dataType: 'date',
  },
]);
const registryResultsPaginated = ref([]);
const dataTableKey = ref(1);
const paginationKey = ref(1);
const fireTagLinkElement = ref({
  name: 'registry results sort name',
  occasionType: 'registry results sort event type',
  formattedOccasionDate: 'registry results sort date',
});

const envPropsStore = useEnvPropsStore();
const searchRegistryStore = useSearchRegistryStore();
const cdnUrl = envPropsStore.envProps?.cdnUrl || '';

const mcomBabyIcon = `${cdnUrl}/img/MCOM_occasion_icon.webp`;
const bcomBabyIcon = `${cdnUrl}/img/Bloomingdales_Baby_Registry_Default_Thumbnail_WEB_MEW.webp`;
const mcomWeddingIcon = `${cdnUrl}/img/rings.svg`;
const bcomWeddingIcon = `${cdnUrl}/img/Bloomingdales_Wedding_Registry_Default_Thumbnail_WEB_MEW.webp`;

const isMcom = computed(() => envPropsStore.envProps.entryPoint.toUpperCase() === 'MCOM');

const registryIcon = (occasionType) => {
  const isBabyRegistry = occasionType.toUpperCase() === 'BABY';
  if (isBabyRegistry && isMcom.value) {
    return mcomBabyIcon;
  }
  if (isBabyRegistry && !isMcom.value) {
    return bcomBabyIcon;
  }
  if (!isBabyRegistry && isMcom.value) {
    return mcomWeddingIcon;
  }
  return bcomWeddingIcon;
};

const registryResults = computed(() => {
  const { searchRegistryResults } = searchRegistryStore;
  return searchRegistryResults.map((item) => {
    const formattedOccasionDate = Object.hasOwn(item, 'occasionDate') && item.occasionDate ? registryFormatDate(item.occasionDate) : '';
    const registrantMiddle = item.registrantMiddleName ? ` ${item.registrantMiddleName}` : '';
    const registrantName = `${item.registrantfirstName}${registrantMiddle} ${item.registrantLastName}`;

    const coregistrantMiddle = item.coregistrantMiddleName ? ` ${item.coregistrantMiddleName}` : '';
    const coregistrantName = item.coregistrantFirstName ? `& ${item.coregistrantFirstName}${coregistrantMiddle} ${item.coregistrantLastName}` : '';

    const iconSrc = registryIcon(item.occasionType);

    const name = `<div class="d-flex align-middle name-col"><div class="registry-image-container"><img src="${iconSrc}" alt="RegistrantIcon" class="registry-image" /></div>
    <div class="large" data-fs-mask>${registrantName} ${coregistrantName} </div></div>`;

    return {
      ...item,
      formattedOccasionDate,
      name,
    };
  });
});
const registryResultsTableVisible = computed(() => searchRegistryStore.registryResultsTableVisible);
const registryResultsLoading = computed(() => searchRegistryStore.registryResultsLoading);

const formatName = (name) => name.trim().split(/\s+/).join('_');

const redirectPath = ({ registryId, registrantfirstName, registrantLastName, coregistrantFirstName, coregistrantLastName }) => {
  let path = `/registry/${formatName(registrantfirstName.originalValue)}-${formatName(registrantLastName.originalValue)}`;
  if (coregistrantFirstName?.originalValue && coregistrantLastName?.originalValue) {
    path += `-${formatName(coregistrantFirstName.originalValue)}-${formatName(coregistrantLastName.originalValue)}`;
  }
  path += `/${registryId.originalValue}`;
  return path;
};

const onRegistrySelect = ({ data }) => {
  searchRegistryStore[SET_SELECTED_REGISTRY_ID](data.registryId.originalValue);
  searchRegistryStore[SET_SELECTED_REGISTRY_GUID](data.registryGUID.originalValue);
  searchRegistryStore[SET_SEARCH_TO_GIFTGIVERPAGE](true);
  searchRegistryStore[SET_GIFTGIVER_TO_SEARCHPAGE](false);

  router.push(redirectPath(data));
};

const handlePageChange = (pageSize, pageNumber, change) => {
  if (!change) {
    window.scrollTo(0, 0);
  }
  registryResultsPaginated.value = paginate(registryResults.value, pageSize, pageNumber);
  dataTableKey.value += 1;
};

const registryResultsWatch = () => {
  paginationKey.value += 1;
  handlePageChange(20, 1, true);
  fireTag('view', {
    page_name: 'registry search results',
    page_type: 'registry',
    num_of_reg_results: registryResults.value.length,
  });
};

const registryResultsTableVisibleWatch = () => {
  paginationKey.value += 1;
  handlePageChange(20, 1, true);
};

watch(() => registryResults.value, registryResultsWatch);

watch(() => registryResultsTableVisible.value, registryResultsTableVisibleWatch);

const onRegistrySort = (event) => {
  if (isMcom.value) {
    fireTag('link', {
      event_name: 'registry results sort',
      link_name: fireTagLinkElement.value[event.sortField.replace('.sortValue', '')],
    });
  } else {
    fireTag('link', {
      event_name: fireTagLinkElement.value[event.sortField.replace('.sortValue', '')],
      link_name: 'registry results sort',
    });
  }
};

onMounted(() => {
  if (registryResults.value.length > 0) {
    registryResultsWatch();
    registryResultsTableVisibleWatch();
  }
});
</script>

<style lang="scss" scoped>
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';
.registry-table {
  :deep(.registry-image-container) {
    width: rem-calc(50);
    height: rem-calc(50);
    border-radius: 50%;
    background-color: $background-color;
  }
  :deep(.registry-image) {
    mix-blend-mode: multiply;
    height: inherit;
    border-radius: 50%;
  }
  :deep(.p-datatable .p-datatable-tbody > tr > td:first-child > .p-column-title) {
    display: none !important;
  }
  :deep(.p-datatable-thead > tr > th.p-sortable-column) {
    padding-left: rem-calc(0);
  }
  :deep(.d-flex) {
    display: flex;
  }
  @include breakpoint(medium up) {
    margin-bottom: rem-calc(73);
    :deep(.p-datatable .p-datatable-thead > tr > th) {
      border-bottom: $black-border;
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td) {
      padding-left: 0;
    }
  }
  @include breakpoint(small down) {
    margin-bottom: rem-calc(41);
    :deep(.p-datatable .p-datatable-tbody > tr > td) {
      padding-left: rem-calc(16);
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(2)) {
      padding-bottom: rem-calc(0);
    }
    :deep(.p-datatable.p-datatable-lg .p-datatable-tbody > tr > td:last-child) {
      padding-top: rem-calc(5);
    }
    :deep(.p-datatable-dropdown) {
      @include padding-direction('bottom', map-get($spacer, 's'));
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td .name-col) {
      flex-wrap: nowrap;
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td:first-child) {
      margin-bottom: rem-calc(28);
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td:nth-child(2)) {
      padding-bottom: rem-calc(0);
    }
    :deep(tbody.p-datatable-tbody > tr > td span) {
      font-weight: 400;
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td:first-child, .p-datatable .p-datatable-tbody > tr > td:first-child) {
      padding-top: rem-calc(20);
    }
    :deep(.p-datatable .p-datatable-tbody > tr > td:last-child, .p-datatable .p-datatable-tbody > tr > td:last-child) {
      padding-bottom: rem-calc(20);
    }
    :deep(.p-datatable .p-datatable-tbody > tr:last-child > td) {
      border-bottom: none;
    }
    :deep(.p-datatable-tbody > tr > td span.p-column-title) {
      font-weight: 500;
    }
  }
  @include breakpoint(xlarge down) {
    :deep(.p-datatable-tbody > tr > td span) {
      font-weight: 500;
    }
  }
  :deep(.registry-image-container) {
    @include breakpoint(large up) {
      margin-right: rem-calc(8);
    }
    @include breakpoint(medium down) {
      margin-right: rem-calc(16);
    }
    @include breakpoint(medium up) {
      margin-left: rem-calc(10);
    }
  }
}
@include breakpoint(medium up) {
  :deep(.pagination-wrap) {
    justify-content: space-between;
    .filtered-pagination {
      width: 50%;
      justify-content: end;
    }
  }
}
@include breakpoint(small down) {
  :deep(.pagination-wrap) {
    justify-content: space-around;
    .filtered-pagination {
      display: none;
    }
  }
}
:deep(.p-datatable-responsive-stack .p-datatable-wrapper table tbody tr td .p-column-title) {
  flex: 0 0 42%;
}
.registry-pagination {
  @include breakpoint(small down) {
    margin-bottom: rem-calc(16);
  }
  @include breakpoint(medium up) {
    margin-bottom: rem-calc(48);
  }
  margin-left: rem-calc(64);
}
.no-registries-info {
  margin-bottom: rem-calc(65);
}
.registries-info {
  @include breakpoint(small down) {
    margin-top: rem-calc(8);
    margin-bottom: rem-calc(8);
  }
  @include breakpoint(medium up) {
    margin-top: rem-calc(24);
  }
}
.pagination-hide {
  display: none;
}
</style>
